/**
 * La colonne de gauche du test — « Ma file N/15 » et « Discussions ».
 *
 * Copie la forme du poste depuis le 14-09-2026 (Kayla : « deux boutons : Ma
 * file, et Discussions — dedans il voit toutes les discussions qui sont
 * libres ») : deux onglets soulignés (`ConversationsPanel`, `ongletDuPoste`),
 * et dans « Discussions » une ligne par fan libre, son signal et le bouton
 * « Ajouter » (`DiscussionsLibres`). Une ligne de « Discussions » ne s'OUVRE
 * pas — le seul geste est « Ajouter », comme au poste ; on n'ouvre que ce qui
 * est dans sa file.
 *
 * ⛔ Pas les vrais composants : ils lisent la pile du serveur, les verrous
 * Convex et la session. Ici tout se dérive de l'état de la simulation.
 */
import { Plus } from "@/shared/ui/icons";
import { Badge, Button, Carte, Conversation, Onglets, Vide, type TonBadge } from "@/shared/ui";

import { Halo } from "./CoupDePouce";
import type { Etat, Message } from "./moteur";
import { FANS, PLAFOND_DE_LA_FILE, type FanDuTest } from "./scenario";

export type VueDeLaListe = "file" | "discussions";

/**
 * 🎨 Le signal d'un fan (`SIGNAL_COLOR` du poste) : ce qui commence prend la
 * marque, un état neutre reste gris.
 */
const TON_DU_SIGNAL: Record<FanDuTest["signal"], TonBadge> = {
  nouveau: "marque",
  répondu: "gris",
  "à relancer": "gris",
};

function dernierMessage(fil: readonly Message[] | undefined): string {
  const m = [...(fil ?? [])].reverse().find((x) => x.de !== "systeme" && x.texte);
  return m ? (m.de === "chatteur" ? `Toi : ${m.texte}` : m.texte) : "";
}

interface Props {
  etat: Etat;
  vue: VueDeLaListe;
  onVue: (v: VueDeLaListe) => void;
  onAjouter: (fanId: string) => void;
  onOuvrir: (fanId: string) => void;
  /** Le rond du tutoriel : sur l'onglet « Discussions », ou sur « Ma file ». */
  vise: "discussions" | "ma_file" | null;
}

export function ListeDesFans({ etat, vue, onVue, onAjouter, onOuvrir, vise }: Props) {
  const libres = FANS.filter((f) => !etat.file.includes(f.id));
  const maFile = etat.file.map((id) => FANS.find((f) => f.id === id)!).filter(Boolean);

  return (
    <Carte
      remplissage="aucun"
      role="region"
      aria-label="Tes conversations"
      className="flex min-h-[220px] w-full shrink-0 flex-col min-[900px]:min-h-0 min-[900px]:w-[clamp(290px,24%,340px)]"
    >
      {/* Les deux onglets du poste. Le rond du tutoriel se pose sur MOITIÉ de la
          rangée : les deux segments sont égaux (`pleineLargeur`). */}
      <div className="relative shrink-0 p-3">
        <Onglets
          style="segments"
          taille="md"
          pleineLargeur
          nom="Ma file ou Discussions"
          valeur={vue}
          onChange={onVue}
          options={[
            { valeur: "file", etiquette: `Ma file ${etat.file.length}/${PLAFOND_DE_LA_FILE}` },
            { valeur: "discussions", etiquette: `Discussions ${libres.length}` },
          ]}
        />
        <Halo
          actif={vise !== null}
          forme="rond"
          className={`pointer-events-none absolute inset-y-3 w-[calc(50%-12px)] ${vise === "discussions" ? "right-3" : "left-3"}`}
        >
          {null}
        </Halo>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {vue === "file" &&
          (maFile.length === 0 ? (
            <Vide
              taille="sm"
              titre="Ta file est vide."
              texte="Va dans « Discussions » pour ajouter des fans."
            />
          ) : (
            <Conversation.ListeDeConversations ariaLabel="Ma file">
              {maFile.map((f) => (
                <Conversation.LigneDeConversation
                  key={f.id}
                  nom={f.nom}
                  avatar={{ graine: f.id }}
                  etiquettes={<Signal fan={f} />}
                  ecoule={f.ilYa}
                  apercu={dernierMessage(etat.fils[f.id])}
                  actif={etat.ouvert === f.id}
                  onClick={() => onOuvrir(f.id)}
                />
              ))}
            </Conversation.ListeDeConversations>
          ))}

        {vue === "discussions" &&
          (libres.length === 0 ? (
            <Vide taille="sm" titre="Aucune discussion disponible." />
          ) : (
            <Conversation.ListeDeConversations ariaLabel="Discussions">
              {libres.map((f) => (
                <Conversation.LigneDeConversation
                  key={f.id}
                  nom={f.nom}
                  avatar={{ graine: f.id }}
                  etiquettes={<Signal fan={f} />}
                  ecoule={f.ilYa}
                  apercu={dernierMessage(etat.fils[f.id])}
                  valeur={
                    <Button
                      size="lg"
                      className="min-h-9"
                      onClick={() => onAjouter(f.id)}
                      disabled={etat.file.length >= PLAFOND_DE_LA_FILE}
                      aria-label={`Ajouter ${f.nom}`}
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden />
                      Ajouter
                    </Button>
                  }
                />
              ))}
            </Conversation.ListeDeConversations>
          ))}
      </div>
    </Carte>
  );
}

/* 📐 Le signal du fan, une pastille à côté du nom (`etiquettes` de la ligne) : le nom, le signal
   et le temps écoulé sur la première ligne, le dernier message dessous. */
function Signal({ fan }: { fan: FanDuTest }) {
  return <Badge ton={TON_DU_SIGNAL[fan.signal]}>{fan.signal}</Badge>;
}
